import { LanguageContext } from "context/LanguageContext"
import { UploadContext } from "context/UploadContext"
import isDisposableEmail from "lib/disposable-email-domains"
import Button from "lib/components/Button"
import Input from "lib/components/Input"
import InputFile from "lib/components/InputFile"
import InputPhone from "lib/components/InputPhone"
import Select from "lib/components/Select"
import TextArea from "lib/components/TextArea"
import fetcher from "lib/fetcher"
import {
  isLinkedinUrl,
  isValidName,
  normalizeLinkedinUrl,
  sanitizeName,
  sanitizeText,
} from "lib/helpers"
import useRecaptcha from "hooks/useRecaptcha"
import React, { useContext, useMemo, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import useSWR from "swr"

function JoinTrainerForm() {
  const { uploadFile, progressNumber } = useContext(UploadContext)
  const { useTranslation: t } = useContext(LanguageContext)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [dataCategory, setDataCategory] = useState([])
  const [loadingUpload, setLoadingUpload] = useState(false)

  const {
    ref: captchaRef,
    isPassed: isRecaptchaPassed,
    getToken: getCaptchaToken,
    reset: resetCaptcha,
  } = useRecaptcha()

  const methods = useForm({
    defaultValues: {
      first_name: "",
      last_name: "",
      source: null,
      priority: null,
      pic_tm: null,
      expertise: [],
      resume_url: null,
      resume_secure_url: null,
      company_name: null,
      job_title: null,
      work_email: null,
      phone_number: null,
      linkedin_profile: null,
      notes: null,
      ktp_url: null,
      status: null,
      reject_reason: null,
    },
    shouldFocusError: true,
  })

  const { data: categoryList } = useSWR("/api/categories/list", fetcher)

  useMemo(() => {
    if (categoryList?.categories.length > 0) {
      const categoryData = categoryList?.categories.map((category) => {
        const optionsData = [...category.options, { label: category.label, value: category.value }]
        return {
          label: category.label,
          value: category.value,
          options: optionsData,
        }
      })
      setDataCategory(categoryData)
    }
    return () => {
      setDataCategory([])
    }
  }, [categoryList])

  const onSubmit = async (data) => {
    setLoadingSubmit(true)
    try {
      const listDataExpertise = data.expertise.map((item) => item.value)
      // Sanitize all user inputs to prevent XSS
      const dataPayload = {
        first_name: sanitizeName(data.first_name, 25),
        last_name: sanitizeName(data.last_name, 25),
        expertise: listDataExpertise,
        cv_resume: data.resume_url || null,
        company_name: data.company_name ? sanitizeText(data.company_name, 200) : null,
        job_title: data.job_title ? sanitizeText(data.job_title, 200) : null,
        work_email: data.work_email,
        phone_number: data.phone_number,
        linkedin_profile: normalizeLinkedinUrl(data.linkedin_profile) || null,
        notes: data.notes ? sanitizeText(data.notes, 2000) : null,
        recaptcha_token: getCaptchaToken(),
      }

      await fetcher("/api/trainer/create", "POST", dataPayload)
      toast.success(t("we_already_received_your_message"))
      setLoadingSubmit(false)
      methods.reset({
        first_name: "",
        last_name: "",
        source: null,
        priority: null,
        pic_tm: null,
        expertise: [],
        resume_url: null,
        resume_secure_url: null,
        company_name: null,
        job_title: null,
        work_email: null,
        phone_number: null,
        linkedin_profile: null,
        notes: null,
        ktp_url: null,
        status: null,
        reject_reason: null,
      })
    } catch (err) {
      setLoadingSubmit(false)
      // Input validation runs before the Hasura mutation, so a rejection never consumed the
      // reCAPTCHA token — keep it so the user doesn't have to solve the challenge again.
      if (err.message !== "Invalid input detected") resetCaptcha()
      if (err.message === "ErrTrainerNameAlreadyExist") {
        toast.error(t("same_name"))
      } else if (err.message === "Invalid input detected") {
        toast.error(t("invalid_input_detected_hint"))
      } else if (err.message === "ErrInvalidRecaptcha") {
        toast.error(t("invalid_recaptcha"))
      } else {
        toast.error(`Oops: ${err.message}`)
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-x-4 ml-0 sm:ml-10 mt-0 sm:-mt-4">
          <div className="form-control">
            <Input
              label={`${t("first_name")} *`}
              placeholder={t("input_first_name")}
              type="text"
              error={!!methods.formState.errors.first_name}
              message={
                methods.formState.errors.first_name &&
                methods.formState.errors.first_name.message &&
                typeof methods.formState.errors.first_name.message === "string"
                  ? methods.formState.errors.first_name.message
                  : ""
              }
              {...methods.register("first_name", {
                required: t("first_name_required"),
                maxLength: {
                  value: 25,
                  message: t("first_name_max_length") || "First name is too long",
                },
                validate: (value) =>
                  isValidName(value, 25) || t("invalid_name_format") || "Invalid name format",
              })}
            />
          </div>
          <div className="form-control">
            <Input
              label={`${t("last_name")} *`}
              placeholder={t("input_last_name")}
              type="text"
              error={!!methods.formState.errors.last_name}
              message={
                methods.formState.errors.last_name &&
                methods.formState.errors.last_name.message &&
                typeof methods.formState.errors.last_name.message === "string"
                  ? methods.formState.errors.last_name.message
                  : ""
              }
              {...methods.register("last_name", {
                required: t("last_name_required"),
                maxLength: {
                  value: 25,
                  message: t("last_name_max_length") || "Last name is too long",
                },
                validate: (value) =>
                  isValidName(value, 25) || t("invalid_name_format") || "Invalid name format",
              })}
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("job_title")} *`}
              placeholder={t("input_job_title")}
              type="text"
              error={!!methods.formState.errors.job_title}
              message={
                methods.formState.errors.job_title &&
                methods.formState.errors.job_title.message &&
                typeof methods.formState.errors.job_title.message === "string"
                  ? methods.formState.errors.job_title.message
                  : ""
              }
              {...methods.register("job_title", {
                required: t("job_title_required"),
              })}
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("email")} *`}
              placeholder={t("input_email")}
              type="text"
              error={!!methods.formState.errors.work_email}
              message={
                methods.formState.errors.work_email &&
                methods.formState.errors.work_email.message &&
                typeof methods.formState.errors.work_email.message === "string"
                  ? methods.formState.errors.work_email.message
                  : ""
              }
              {...methods.register("work_email", {
                required: t("email_required"),
                pattern: {
                  value: /^[A-Z0-9._]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("invalid_email_address"),
                },
                validate: (value) =>
                  value && isDisposableEmail(value) ? t("invalid_email_address") : true,
              })}
            />
          </div>
          <div className="col-span-2 form-control">
            <Controller
              control={methods.control}
              name="phone_number"
              rules={{
                required: t("phone_number_required"),
                minLength: {
                  value: 8,
                  message: t("phone_number_must_match_rule"),
                },
                maxLength: {
                  value: 14,
                  message: t("phone_number_must_match_rule"),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <InputPhone
                  defaultCountry="id"
                  countryCodeEditable
                  enableSearch
                  disableSearchIcon
                  label={`${t("phone_number")} *`}
                  placeholder="628122XXXXXXX"
                  error={!!methods.formState.errors.phone_number}
                  message={
                    methods.formState.errors.phone_number &&
                    methods.formState.errors.phone_number.message &&
                    typeof methods.formState.errors.phone_number.message === "string"
                      ? methods.formState.errors.phone_number.message
                      : ""
                  }
                  value={value}
                  onChange={(event) => {
                    onChange(event)
                    methods.trigger("phone_number")
                  }}
                />
              )}
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={t("company_name")}
              placeholder={t("input_company_name")}
              type="text"
              {...methods.register("company_name")}
            />
          </div>
          <div className="col-span-2 form-control">
            <Input
              label={`${t("linkedin_profile")} *`}
              placeholder={t("input_linkedin_profile")}
              type="text"
              error={!!methods.formState.errors.linkedin_profile}
              message={
                methods.formState.errors.linkedin_profile &&
                methods.formState.errors.linkedin_profile.message &&
                typeof methods.formState.errors.linkedin_profile.message === "string"
                  ? methods.formState.errors.linkedin_profile.message
                  : ""
              }
              {...methods.register("linkedin_profile", {
                required: t("linkedin_profile_required"),
                // `required` passes on whitespace-only input, which would normalize to null on a
                // required field — reject blank-after-trim explicitly.
                validate: (value) =>
                  (normalizeLinkedinUrl(value)
                    ? isLinkedinUrl(value) ||
                      t("invalid_linkedin_profile") ||
                      "This is not a LinkedIn link"
                    : t("linkedin_profile_required") || "LinkedIn profile required!"),
              })}
            />
          </div>
          <div className="col-span-2 form-control">
            <Select
              isMulti
              name="expertise"
              options={dataCategory}
              label={`${t("expertise")} *`}
              placeholder={t("input_expertise")}
              {...methods.register("expertise", {
                required: t("expertise_required"),
              })}
              value={methods.watch("expertise")}
              onChange={(eventTarget) => {
                methods.setValue("expertise", eventTarget, { shouldDirty: true })
                methods.clearErrors("expertise")
              }}
              maxLimit={3}
              error={!!methods.formState.errors.expertise}
              message={
                methods.formState.errors.expertise &&
                methods.formState.errors.expertise.message &&
                typeof methods.formState.errors.expertise.message === "string"
                  ? methods.formState.errors.expertise.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2 form-control mt-4">
            <Controller
              control={methods.control}
              name="resume_url"
              rules={{
                required: t("resume_required"),
              }}
              render={({ field: { value } }) => (
                <InputFile
                  label="CV/Resume *"
                  name="resume_url"
                  placeholder=""
                  value={value}
                  loading={loadingUpload}
                  progressNumber={progressNumber}
                  error={!!methods.formState.errors.resume_url}
                  message={
                    methods.formState.errors.resume_url &&
                    methods.formState.errors.resume_url.message &&
                    typeof methods.formState.errors.resume_url.message === "string"
                      ? methods.formState.errors.resume_url.message
                      : ""
                  }
                  onChange={async (event) => {
                    setLoadingUpload(true)
                    await uploadFile(
                      event.target.files[0],
                      (responseData) => {
                        if (responseData.secure_url) {
                          methods.setValue("resume_url", responseData.secure_url, {
                            shouldDirty: true,
                          })
                          setLoadingUpload(false)
                          methods.clearErrors("resume_url")
                        }
                      },
                      "trm",
                      false,
                    )
                    event.target.value = null
                  }}
                  onCancel={() => {
                    methods.setValue("resume_url", null)
                    setLoadingUpload(false)
                  }}
                  accept="image/*, application/pdf"
                />
              )}
            />
          </div>
          <div className="col-span-2 form-control">
            <TextArea
              label={`${t("things_we_need")} *`}
              placeholder={t("input_things_we_need")}
              rows={8}
              {...methods.register("notes", {
                required: t("things_we_need_required"),
              })}
              error={!!methods.formState.errors.notes}
              message={
                methods.formState.errors.notes &&
                methods.formState.errors.notes.message &&
                typeof methods.formState.errors.notes.message === "string"
                  ? methods.formState.errors.notes.message
                  : ""
              }
            />
          </div>
          <div className="col-span-2">
            <div className="flex items-center">
              <div className="hover:cursor-pointer" ref={captchaRef} />
            </div>
          </div>
          <div className="mt-4">
            {isRecaptchaPassed ? (
              <Button
                disabled={loadingSubmit}
                loading={loadingSubmit}
                type="submit"
                color="primary"
                variant="contained"
                fullWidth>
                {t("submit")}
              </Button>
            ) : (
              <Button disabled type="submit" color="primary" variant="contained" fullWidth>
                {t("submit")}
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default JoinTrainerForm
